import { supabase } from './supabase.js'
import { snakeToCamel, getEmpresaId } from './syncManager.js'

// Build v24 - cache bust - queries sem tipo=epi coluna direta

export async function getDashboardKPIs() {
  const empresaId = getEmpresaId()
  console.log('[Dashboard] empresaId:', empresaId)
  
  // Verificar sessão Supabase
  const { data: { session: supaSession } } = await supabase.auth.getSession()
  if (!supaSession) {
    console.error('[Dashboard] ERRO: Sem sessão Supabase JWT - não é possível buscar dados')
    return {
      totalProdutos: 0, totalFuncionarios: 0, pendentes: 0, emManutencao: 0,
      abaixoMinimo: 0, itensZerados: 0, totalSaidas30d: 0, totalPerdas30d: 0,
      taxaPerda: '0.0', taxaDevolucao: '100.0', mediaDiasPendente: 0,
      produtosVencendo: [], alertas: ['Erro: Faça login via Supabase para ver os dados']
    }
  }
  
  const now = Date.now()
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000
  const ninetyDaysAgo = now - 90 * 24 * 60 * 60 * 1000
  const trintaDiasFuturo = now + 30 * 24 * 60 * 60 * 1000

  // Contagens otimizadas com filtros no banco
  const [
    { count: totalProdutos, error: errProdutos },
    { count: totalFuncionarios, error: errFuncionarios },
    { count: pendentes, error: errPendentes },
    { count: emManutencao, error: errManutencao },
    { count: abaixoMinimo, error: errAbaixoMinimo },
    { count: itensZerados, error: errZerados },
    { data: produtosVencendoData, error: errVencendo },
    { data: movimentacoesData, error: errMovimentacoes }
  ] = await Promise.all([
    // Total produtos
    supabase.from('produtos').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId),
    // Funcionários ativos
    supabase.from('funcionarios').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId).eq('status', 'ativo'),
    // Empréstimos pendentes
    supabase.from('movimentacoes').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId).eq('status', 'pendente-devolucao'),
    // Em manutenção
    supabase.from('movimentacoes').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId).eq('status', 'em-manutencao'),
    // Produtos abaixo do mínimo
    supabase.from('produtos').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId).lt('quantidade', 1),
    // Produtos sem estoque
    supabase.from('produtos').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId).eq('quantidade', 0),
    // Produtos EPI com data de validade (filtrar vencendo no cliente)
    supabase.from('produtos').select('id,nome,dados,data_validade,quantidade').eq('empresa_id', empresaId).not('data_validade', 'is', null).order('data_validade').limit(50),
    // Movimentações dos últimos 90 dias (filtro no banco via criado_em)
    supabase.from('movimentacoes').select('tipo,status,quantidade,criado_em,produto_id,produto_nome,funcionario_id,funcionario_nome').eq('empresa_id', empresaId).gte('criado_em', ninetyDaysAgo).limit(500)
  ])

  // Log de erros das queries para debug
  const errors = [
    { name: 'produtos', err: errProdutos },
    { name: 'funcionarios', err: errFuncionarios },
    { name: 'pendentes', err: errPendentes },
    { name: 'manutencao', err: errManutencao },
    { name: 'abaixoMinimo', err: errAbaixoMinimo },
    { name: 'zerados', err: errZerados },
    { name: 'vencendo', err: errVencendo },
    { name: 'movimentacoes', err: errMovimentacoes }
  ].filter(e => e.err)
  if (errors.length > 0) {
    console.error('[Dashboard] Erros nas queries:', errors.map(e => `${e.name}: ${e.err.message}`).join(', '))
  }

  // Processamento dos dados com filtros de data no cliente
  const produtosVencendo = (produtosVencendoData || [])
    .filter(p => p.dados?.tipo === 'epi' && p.data_validade > now && p.data_validade <= trintaDiasFuturo)
    .slice(0, 10)
    .map(snakeToCamel)
  
  const movimentacoes = (movimentacoesData || []).map(snakeToCamel)

  const movRecentes = movimentacoes.filter(m => (m.criadoEm ?? 0) >= thirtyDaysAgo)
  const mov90d = movimentacoes.filter(m => (m.criadoEm ?? 0) >= ninetyDaysAgo)

  // Cálculos leves em memória
  const pendentesRaw = movimentacoes.filter(m => m.status === 'pendente-devolucao')

  const totalSaidas = movRecentes
    .filter(m => m.tipo === 'saida' || m.tipo === 'emprestimo')
    .reduce((acc, m) => acc + Number(m.quantidade ?? 0), 0)

  const totalPerdas = mov90d
    .filter(m => m.status === 'perdido')
    .reduce((acc, m) => acc + Number(m.quantidade ?? 0), 0)

  const taxaPerda = totalSaidas > 0 ? ((totalPerdas / totalSaidas) * 100).toFixed(1) : '0.0'

  // Taxa de devolução
  const emprestimos90d = mov90d.filter(m => m.tipo === 'emprestimo')
  const devolvidos90d = emprestimos90d.filter(m => m.status === 'concluido').length
  const taxaDevolucao = emprestimos90d.length > 0 ? ((devolvidos90d / emprestimos90d.length) * 100).toFixed(1) : '100.0'

  // Média de dias pendente
  let mediaDiasPendente = 0
  if (pendentesRaw.length > 0) {
    const totalDias = pendentesRaw.reduce((acc, m) => {
      const dias = Math.floor((now - (m.criadoEm ?? now)) / (24 * 60 * 60 * 1000))
      return acc + dias
    }, 0)
    mediaDiasPendente = Math.round(totalDias / pendentesRaw.length)
  }

  const result = {
    totalProdutos: totalProdutos || 0,
    totalFuncionarios: totalFuncionarios || 0,
    pendentes: pendentes || 0,
    emManutencao: emManutencao || 0,
    abaixoMinimo: abaixoMinimo || 0,
    itensZerados: itensZerados || 0,
    totalSaidas30d: totalSaidas,
    totalPerdas30d: totalPerdas,
    taxaPerda,
    taxaDevolucao,
    mediaDiasPendente,
    produtosVencendo,
    alertas: [
      ...(abaixoMinimo > 0 ? [`${abaixoMinimo} produtos abaixo do mínimo`] : []),
      ...(itensZerados > 0 ? [`${itensZerados} produtos sem estoque`] : []),
      ...(pendentes > 0 ? [`${pendentes} empréstimos pendentes`] : []),
      ...(mediaDiasPendente > 7 ? [`Média ${mediaDiasPendente} dias de atraso`] : []),
      ...(produtosVencendo.length > 0 ? [`${produtosVencendo.length} produtos próximos ao vencimento`] : [])
    ]
  }
  console.log('[Dashboard] Dados carregados:', { totalProdutos: result.totalProdutos, totalFuncionarios: result.totalFuncionarios, errors: errors.length })
  return result
}

export async function getConsumoPorFuncionario({ dias = 30 } = {}) {
  const empresaId = getEmpresaId()
  const since = Date.now() - dias * 24 * 60 * 60 * 1000
  
  const { data } = await supabase
    .from('movimentacoes')
    .select('funcionario_id,funcionario_nome,quantidade,produto_nome,criado_em')
    .eq('empresa_id', empresaId)
    .in('tipo', ['saida', 'emprestimo', 'entrega'])
    .limit(500)
  
  const movs = (data || [])
    .filter(m => (m.criado_em ?? 0) >= since)
    .map(snakeToCamel)
  const map = new Map()
  
  movs.forEach(m => {
    const id = m.funcionarioId
    if (!map.has(id)) {
      map.set(id, { funcionarioId: id, funcionarioNome: m.funcionarioNome, total: 0, itens: [] })
    }
    const entry = map.get(id)
    entry.total += Number(m.quantidade ?? 0)
    entry.itens.push({ produto: m.produtoNome, quantidade: m.quantidade, data: m.criadoEm })
  })

  return Array.from(map.values()).sort((a, b) => b.total - a.total)
}


export async function getEPIsVencidos() {
  const empresaId = getEmpresaId()
  const agora = Date.now()
  
  const { data } = await supabase
    .from('produtos')
    .select('id,nome,dados,data_validade,quantidade')
    .eq('empresa_id', empresaId)
    .not('data_validade', 'is', null)
    .order('data_validade')
    .limit(100)
  
  return (data || [])
    .filter(p => p.dados?.tipo === 'epi' && p.data_validade <= agora)
    .slice(0, 50)
    .map(snakeToCamel)
}

export async function getEPIsPrestesVencer() {
  const empresaId = getEmpresaId()
  const agora = Date.now()
  const trintaDias = agora + 30 * 24 * 60 * 60 * 1000
  
  const { data } = await supabase
    .from('produtos')
    .select('id,nome,dados,data_validade,quantidade')
    .eq('empresa_id', empresaId)
    .not('data_validade', 'is', null)
    .order('data_validade')
    .limit(100)
  
  return (data || [])
    .filter(p => p.dados?.tipo === 'epi' && p.data_validade > agora && p.data_validade <= trintaDias)
    .slice(0, 50)
    .map(snakeToCamel)
}

export async function getTopPerdas({ dias = 30, limit = 10 } = {}) {
  const empresaId = getEmpresaId()
  const since = Date.now() - dias * 24 * 60 * 60 * 1000
  
  const { data } = await supabase
    .from('movimentacoes')
    .select('id,produto_nome,funcionario_nome,quantidade,data_devolucao,criado_em')
    .eq('empresa_id', empresaId)
    .eq('status', 'perdido')
    .limit(limit)
  
  return (data || [])
    .filter(m => (m.criado_em ?? 0) >= since)
    .map(snakeToCamel)
}

export async function getConsumoPorProduto({ dias = 30 } = {}) {
  const empresaId = getEmpresaId()
  const since = Date.now() - dias * 24 * 60 * 60 * 1000
  
  const { data } = await supabase
    .from('movimentacoes')
    .select('produto_id,produto_nome,quantidade,tipo,criado_em')
    .eq('empresa_id', empresaId)
    .in('tipo', ['saida', 'entrega'])
    .limit(500)
  
  const movs = (data || [])
    .filter(m => (m.criado_em ?? 0) >= since)
    .map(snakeToCamel)
  const map = new Map()
  
  movs.forEach(m => {
    const pid = m.produtoId
    if (!map.has(pid)) {
      map.set(pid, { produtoId: pid, produtoNome: m.produtoNome, tipo: m.tipo, total: 0 })
    }
    map.get(pid).total += Number(m.quantidade ?? 0)
  })

  return Array.from(map.values()).sort((a, b) => b.total - a.total)
}

// Comparativos de período - otimizado com queries filtradas
export async function getPeriodComparison(periodDays = 30) {
  const empresaId = getEmpresaId()
  const now = Date.now()
  const currentStart = now - (periodDays * 24 * 60 * 60 * 1000)
  const previousStart = currentStart - (periodDays * 24 * 60 * 60 * 1000)
  
  // Buscar movimentações recentes (filtrar período no cliente)
  const { data: allData } = await supabase
    .from('movimentacoes')
    .select('tipo,quantidade,produto_id,funcionario_id,criado_em')
    .eq('empresa_id', empresaId)
    .limit(1000)
  
  const allMovs = (allData || [])
    .filter(m => (m.criado_em ?? 0) >= previousStart)
    .map(snakeToCamel)
  
  const currentMovs = allMovs.filter(m => m.criadoEm >= currentStart)
  const previousMovs = allMovs.filter(m => m.criadoEm >= previousStart && m.criadoEm < currentStart)
  
  const calcMetrics = (movs) => ({
    totalEntregas: movs.filter(m => m.tipo === 'saida' || m.tipo === 'entrega').reduce((acc, m) => acc + (m.quantidade || 0), 0),
    totalEmprestimos: movs.filter(m => m.tipo === 'emprestimo').length,
    totalItens: movs.reduce((acc, m) => acc + (m.quantidade || 0), 0),
    uniqueProducts: new Set(movs.map(m => m.produtoId)).size,
    uniqueEmployees: new Set(movs.map(m => m.funcionarioId)).size
  })
  
  const current = calcMetrics(currentMovs)
  const previous = calcMetrics(previousMovs)
  
  const calcChange = (curr, prev) => {
    if (prev === 0) return curr > 0 ? 100 : 0
    return Math.round(((curr - prev) / prev) * 100)
  }
  
  return {
    periodDays,
    current,
    previous,
    changes: {
      entregas: calcChange(current.totalEntregas, previous.totalEntregas),
      emprestimos: calcChange(current.totalEmprestimos, previous.totalEmprestimos),
      itens: calcChange(current.totalItens, previous.totalItens),
      produtos: calcChange(current.uniqueProducts, previous.uniqueProducts),
      funcionarios: calcChange(current.uniqueEmployees, previous.uniqueEmployees)
    }
  }
}
